"use client";

import { useEffect, useRef, useState } from "react";
import { useLoading } from "@/contexts/LoadingContext";
import { usePathname } from "next/navigation";

interface ScrollAnimationClientProps {
  children: React.ReactNode;
  className?: string;
  threshold?: number;
  delay?: number;
  direction?: "left" | "right" | "up" | "down";
  animationType?: "fade" | "slide" | "scale" | "blur" | "slide-blur";
  duration?: number;
}

export default function ScrollAnimationClient({
  children,
  className = "",
  threshold = 0.2,
  delay = 0,
  direction = "left",
  animationType = "fade",
  duration = 700,
}: ScrollAnimationClientProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { isPageReady } = useLoading();
  const pathname = usePathname();

  useEffect(() => {
    setIsVisible(false);

    if (!isPageReady) return;

    const element = elementRef.current;
    if (!element) return;

    let observer: IntersectionObserver | null = null;
    let rafId2: number | undefined;

    const rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setTimeout(() => {
                  setIsVisible(true);
                }, delay);
                observer?.unobserve(entry.target);
              }
            });
          },
          { threshold }
        );

        // Observe element only once
        observer.observe(element);
      });
    });

    return () => {
      cancelAnimationFrame(rafId1);
      if (rafId2 !== undefined) cancelAnimationFrame(rafId2);
      if (observer && element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, delay, isPageReady, pathname]);

  const getAnimationClasses = () => {
    const baseClasses = `transition-all duration-${duration} ease-out`;

    if (isVisible) {
      return `${baseClasses} opacity-100 translate-x-0 translate-y-0 scale-100 blur-0`;
    }

    let transformClasses = "";
    const opacityClass = "opacity-0";
    const blurClass = "blur-sm";

    switch (direction) {
      case "left":
        transformClasses = "-translate-x-8";
        break;
      case "right":
        transformClasses = "translate-x-8";
        break;
      case "up":
        transformClasses = "-translate-y-8";
        break;
      case "down":
        transformClasses = "translate-y-8";
        break;
    }

    switch (animationType) {
      case "fade":
        return `${baseClasses} ${opacityClass}`;
      case "slide":
        return `${baseClasses} ${opacityClass} ${transformClasses}`;
      case "scale":
        return `${baseClasses} ${opacityClass} scale-95`;
      case "blur":
        return `${baseClasses} ${opacityClass} ${blurClass}`;
      case "slide-blur":
        return `${baseClasses} ${opacityClass} ${transformClasses} ${blurClass}`;
      default:
        return `${baseClasses} ${opacityClass} ${transformClasses} scale-95 ${blurClass}`;
    }
  };

  return (
    <div ref={elementRef} className={`${getAnimationClasses()} ${className}`}>
      {children}
    </div>
  );
}
